import type { SlackLinkMethod } from '@fleet/shared';
import { apiFetch } from './client';

export interface UserSummary {
  id: string;
  displayName: string;
  email?: string;
}

export const usersApi = {
  /**
   * Personen, für die gebucht werden kann.
   *
   * Bewusst nicht die Entra-Verzeichnissuche: Dort stünden auch Menschen, die
   * sich nie angemeldet haben und für die es folglich keinen Datensatz gibt,
   * an dem eine Buchung hängen könnte.
   *
   * Der Endpunkt ist Managern und der Fuhrparkleitung vorbehalten und antwortet
   * sonst mit MISSING_ROLE.
   */
  list: (query?: string) =>
    apiFetch<UserSummary[]>(`/api/v1/users${query ? `?q=${encodeURIComponent(query)}` : ''}`),
};

/** Stand der Slack-Verknüpfung einer Person. */
export interface SlackLinkView {
  slackUserId: string;
  verifiedVia: SlackLinkMethod;
  verifiedAt: string;
  /** Gesetzt heißt: widerrufen. Die Zeile bleibt als Beleg stehen. */
  revokedAt?: string;
}

export interface AdminUserView {
  id: string;
  entraObjectId: string;
  displayName: string;
  userPrincipalName: string;
  email?: string;
  isActive: boolean;
  /** Fehlt bei Personen, die angelegt wurden, sich aber nie angemeldet haben. */
  lastLoginAt?: string;
  slackLink?: SlackLinkView;
}

/** Ein Mitglied des Slack-Workspace, für die Auswahlliste. */
export interface SlackMember {
  id: string;
  displayName: string;
  handle?: string;
  email?: string;
  isGuest: boolean;
}

/**
 * Nutzerverwaltung — ausschließlich für die Administration.
 *
 * Getrennt von `usersApi`, weil dahinter ein anderer Endpunkt mit einer anderen
 * Rechteprüfung steht: `usersApi.list` ist die schmale Personenliste fürs
 * Buchen, hier stehen auch deaktivierte Konten, die Entra-Objekt-ID und der
 * Verknüpfungsstand.
 */
export const usersAdminApi = {
  list: (query?: string) =>
    apiFetch<AdminUserView[]>(
      `/api/v1/admin/users${query ? `?q=${encodeURIComponent(query)}` : ''}`,
    ),

  /**
   * Person aus dem Verzeichnis anlegen.
   *
   * Nur die Objekt-ID: Name, UPN und Mailadresse holt der Server aus Graph.
   * Was hier mitgeschickt würde, könnte niemand belegen.
   */
  provision: (entraObjectId: string) =>
    apiFetch<AdminUserView>('/api/v1/admin/users', {
      method: 'POST',
      body: JSON.stringify({ entraObjectId }),
    }),

  setSlackLink: (userId: string, slackUserId: string) =>
    apiFetch<SlackLinkView>(`/api/v1/admin/users/${userId}/slack-link`, {
      method: 'PUT',
      body: JSON.stringify({ slackUserId }),
    }),

  revokeSlackLink: (userId: string) =>
    apiFetch<void>(`/api/v1/admin/users/${userId}/slack-link`, { method: 'DELETE' }),

  /**
   * Mitglieder des Slack-Workspace.
   *
   * Läuft über unsere API, damit das Bot-Token den Server nicht verlässt.
   * Liefert eine leere Liste, wenn Slack nicht eingerichtet ist — das ist ein
   * zulässiger Zustand und kein Fehler.
   */
  slackMembers: () => apiFetch<SlackMember[]>('/api/v1/admin/users/slack/members'),

  /**
   * Alle offenen Verknüpfungen auf einmal abgleichen.
   *
   * Bis hierher gab es dafür keinen Weg: Der Abgleich lief nur faul und einzeln,
   * angestoßen von einer Zustellung — weshalb in der Liste bei allen ein Strich
   * stand, der wie „hat kein Slack-Konto" aussieht und „wurde nie versucht"
   * heißt.
   */
  matchSlack: () =>
    apiFetch<SlackMatchReport>('/api/v1/admin/users/slack/match', { method: 'POST' }),
};

/** Was ein Massenabgleich ergeben hat — mit Namen, nicht nur Zahlen. */
export interface SlackMatchReport {
  linked: { displayName: string; slackUserId: string; email?: string }[];
  skipped: { displayName: string; reason: 'ALREADY_LINKED' | 'REVOKED' }[];
  /** Mitsamt der geprüften Adressen: Daran sieht man, was fehlt. */
  unmatched: { displayName: string; tried: string[] }[];
  conflicts: { displayName: string; slackUserId: string; belongsTo: string }[];
  /** Gäste werden nicht automatisch verknüpft — das bestätigt ein Mensch. */
  guests: { displayName: string; slackUserId: string; slackName: string }[];
}
